<script setup lang="ts">
import { AmountCurrencySelector } from '~/components/feature/amount-currency-selector'
import type { IMultiselectOption, IResponseData } from '~/types'
import type { ICreditPurposes } from '~/components/widget/select-credit-purpose'
import { useCreditFilters } from '~/components/widget/credits/list-meili-search/mocks'

import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from '@/components/ui/select'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'

const { t } = useI18n()
const { periodOptions } = useCreditFilters()

// State for filters
const selectedAmount = defineModel<number | undefined>('amount')
const selectedCurrency = defineModel<string>('currency', { default: 'kgs' })
const selectedPeriod = defineModel<string>('period', { default: '12' })
const selectedPurpose = defineModel<string>('purpose', { default: '0' })
const selectedForWhom = defineModel<string>('forWhom', { default: 'private_person' })

// Currency store
const currencyStore = useCurrencyStore()

// Currency options for the selector
const currencyOptions = computed<IMultiselectOption[]>(() => {
  const menuItems = ['KGS', 'RUB', 'USD', 'EUR', 'KZT', 'CNY']
  const currencyList = currencyStore.list

  return [
    { id: 0, label: t('common.any_currency'), value: 'any' },
    ...currencyList
      .filter((c) => menuItems.includes(c.code))
      .map((c, i) => ({
        id: i + 1,
        label: c.code,
        value: c.code.toLowerCase()
      }))
  ]
})

// Fetch credit purposes
const { data: creditPurposesData } = useApi<IResponseData<ICreditPurposes[]>>(
  () => '/credit_purposes',
  { key: 'credit-purposes-best-offer' }
)

const creditPurposeOptions = computed<IMultiselectOption[]>(
  () =>
    creditPurposesData.value?.data.map((item) => ({
      id: item.id,
      value: String(item.id),
      label: item.title
    })) ?? []
)

// Fetch currencies on mount
onMounted(() => {
  currencyStore.fetchCurrencies()
})
</script>

<template>
  <div class="sticky top-32 space-y-3">
    <!-- Credit Amount with Currency Selector -->
    <div class="space-y-2">
      <label class="text-sm font-semibold text-gray-700">{{ $t('common.credit_amount') }}</label>
      <AmountCurrencySelector
        v-model:amount="selectedAmount"
        v-model:currency="selectedCurrency"
        :currency-options="currencyOptions"
      />
    </div>

    <!-- For Whom (Toggle Group) -->
    <div class="space-y-2">
      <label class="text-sm font-semibold text-gray-700">{{ $t('common.for_whom') }}</label>
      <ToggleGroup
        v-model="selectedForWhom"
        type="single"
        variant="outline"
        size="lg"
        class="grid w-full grid-cols-2"
      >
        <ToggleGroupItem value="private_person" class="w-full">{{
          $t('common.private_person')
        }}</ToggleGroupItem>
        <ToggleGroupItem value="legal_entity" class="w-full">{{
          $t('common.legal_entity')
        }}</ToggleGroupItem>
      </ToggleGroup>
    </div>

    <!-- Credit Period (Select) -->
    <div class="space-y-2">
      <label class="text-sm font-semibold text-gray-700">{{ $t('common.credit_term') }}</label>
      <Select v-model="selectedPeriod">
        <SelectTrigger class="w-full" variant="searchForm" :aria-label="$t('common.select_period')">
          <SelectValue :placeholder="$t('common.select_period')" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="option in periodOptions" :key="option.id" :value="option.value">
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>

    <!-- Credit Purpose (Select) -->
    <div class="space-y-2">
      <label class="text-sm font-semibold text-gray-700">{{ $t('common.credit_purpose') }}</label>
      <Select v-model="selectedPurpose">
        <SelectTrigger
          class="w-full"
          variant="searchForm"
          :aria-label="$t('form.credit_request.aria_labels.purpose')"
        >
          <SelectValue :placeholder="$t('form.credit_request.aria_labels.purpose')" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="option in creditPurposeOptions" :key="option.id" :value="option.value">
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
</template>
